import React, { useState } from 'react';

import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import EmptyState from '~/components/core/EmptyState';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Text from '~/components/core/TextComponents/Text';
import { AddIcon } from '~/components/deprecatedMuiIcons';

import { useESignConfiguration } from './hooks/useESignConfiguration';
import ESignConfigurationCard from './ESignConfigurationCard';
import ESignConfigurationDialog from './ESignConfigurationDialog';

const ESignConfigurationTab: React.FC = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    isLoading,
    isError,
    configuration,
    hasConfiguration,
    createConfiguration,
    updateConfiguration,
    isSubmitting,
  } = useESignConfiguration();

  const handleCreateClick = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleSubmit = async () => {
    await createConfiguration();
    handleCloseDialog();
  };

  const handleEnableClick = async () => {
    await updateConfiguration(true);
  };

  const handleDisableClick = async () => {
    await updateConfiguration(false);
  };

  if (isLoading || isError) {
    return <SkeletonTable rowsCount={5} columnsCount={6} isError={isError} />;
  }

  return (
    <CardDialog title="E-SIGN CONFIGURATION">
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.PRIMARY}>
        Configure electronic signature integration to enable document signing workflows for your organization. Currently
        supports Dropbox Sign with automatic app provisioning.
      </Text>

      {!hasConfiguration && (
        <div className="flex justify-end">
          <Button color="primary" onClick={handleCreateClick} disabled={isSubmitting}>
            <AddIcon />
            Create Configuration
          </Button>
        </div>
      )}

      {hasConfiguration && configuration ? (
        <ESignConfigurationCard
          configuration={configuration}
          onEnable={handleEnableClick}
          onDisable={handleDisableClick}
          isLoading={isSubmitting}
        />
      ) : (
        <EmptyState
          title="No E-Sign Configuration"
          subtitle="Create a configuration to enable electronic signature functionality with Dropbox Sign. The system will automatically provision an API app for your organization."
        />
      )}

      <ESignConfigurationDialog isOpen={isDialogOpen} onSubmit={handleSubmit} onClose={handleCloseDialog} />
    </CardDialog>
  );
};

export default ESignConfigurationTab;
